<template>
  <v-container>
    <v-card class="mb-4">
      <v-card-title>
        <h1 class="text-h5">Keyboxes</h1>
        <v-spacer></v-spacer>
        <v-btn 
          color="primary" 
          @click="openKeyboxDialog()"
        >
          Create New Keybox
        </v-btn>
      </v-card-title>
      
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="keyboxes"
          :loading="loading"
          :server-items-length="totalItems"
          :items-per-page="itemsPerPage"
          sort-by="updatedAt"
          sort-desc
          class="elevation-1"
        >
          <!-- Status column with colored chip -->
          <template #[`item.isCurrent`]="{ item }">
            <v-chip
              :color="item.isCurrent ? 'success' : 'gray'"
              text-color="white"
              small
            >
              {{ item.isCurrent ? 'Current' : 'Not Current' }}
            </v-chip>
          </template>
          
          <!-- Formatted dates -->
          <template #[`item.createdAt`]="{ item }">
            {{ new Date(item.createdAt).toLocaleString() }}
          </template>
          
          <template #[`item.updatedAt`]="{ item }">
            {{ new Date(item.updatedAt).toLocaleString() }}
          </template>
          
          <!-- Actions column -->
          <template #[`item.actions`]="{ item }">
            <v-btn
              icon
              small
              class="mr-2"
              @click="viewKeybox(item)"
              title="View Details"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            
            <v-btn
              icon
              small
              class="mr-2"
              @click="openKeyboxDialog(item)"
              title="Edit"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            
            <v-btn
              icon
              small
              class="mr-2"
              :disabled="item.isCurrent"
              @click="confirmSetCurrent(item)"
              title="Set as Current"
              v-if="!item.isCurrent"
            >
              <v-icon>mdi-star</v-icon>
            </v-btn>
            
            <v-btn
              icon
              small
              color="error"
              :disabled="item.isCurrent"
              @click="confirmDelete(item)"
              title="Delete"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    
    <!-- Keybox Form Dialog -->
    <v-dialog
      v-model="keyboxDialog"
      max-width="800px"
      persistent
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editMode ? 'Edit Keybox' : 'New Keybox' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-alert
            v-if="!editMode"
            text
            color="info"
            class="mb-4"
          >
            <div class="d-flex align-center">
              <span>Paste JSON keybox data to auto-fill form</span>
              <v-spacer></v-spacer>
              <v-btn
                small
                color="primary"
                @click="showJsonImportDialog = true"
              >
                Import JSON
              </v-btn>
            </div>
          </v-alert>

          <v-form ref="form" v-model="formValid">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedKeybox.name"
                    label="Name"
                    required
                    :placeholder="getDefaultKeybox().name"
                    :rules="[v => !!v || 'Name is required']"
                    hint="Auto-generated using date if left empty"
                    persistent-hint
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12">
                  <v-textarea
                    v-model="editedKeybox.description"
                    label="Description"
                    rows="2"
                    :placeholder="getDefaultKeybox().description"
                    hint="Auto-generated timestamp if left empty"
                    persistent-hint
                  ></v-textarea>
                </v-col>
                
                <v-col cols="12">
                  <v-switch
                    v-model="editedKeybox.isCurrent"
                    label="Set as Current"
                    :disabled="editMode && currentKeybox && currentKeybox._id === editedKeybox._id"
                  ></v-switch>
                </v-col>
                
                <!-- EC Keys Section -->
                <v-col cols="12">
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        EC Keys
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-textarea
                          v-model="editedKeybox.ecPriv"
                          label="EC Private Key"
                          rows="4"
                          hint="PEM format EC private key"
                        ></v-textarea>
                        
                        <div class="mt-3">
                          <div class="d-flex">
                            <h3 class="text-subtitle-1 mb-2">EC Certificates</h3>
                            <v-spacer></v-spacer>
                            <v-btn
                              small
                              color="primary"
                              @click="addCertificate('ec')"
                            >
                              Add Certificate
                            </v-btn>
                          </div>
                          
                          <div 
                            v-for="(cert, index) in editedKeybox.ecCerts" 
                            :key="`ec-cert-${index}`"
                            class="d-flex align-center mb-2"
                          >
                            <v-textarea
                              v-model="editedKeybox.ecCerts[index]"
                              label="Certificate"
                              rows="3"
                              class="mr-2"
                            ></v-textarea>
                            
                            <v-btn
                              icon
                              color="error"
                              @click="removeCertificate('ec', index)"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                          
                          <v-alert
                            v-if="!editedKeybox.ecCerts.length"
                            type="info"
                            text
                            dense
                          >
                            No EC certificates added
                          </v-alert>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
                
                <!-- RSA Keys Section -->
                <v-col cols="12">
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        RSA Keys
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-textarea
                          v-model="editedKeybox.rsaPriv"
                          label="RSA Private Key"
                          rows="4"
                          hint="PEM format RSA private key"
                        ></v-textarea>
                        
                        <div class="mt-3">
                          <div class="d-flex">
                            <h3 class="text-subtitle-1 mb-2">RSA Certificates</h3>
                            <v-spacer></v-spacer>
                            <v-btn
                              small
                              color="primary"
                              @click="addCertificate('rsa')"
                            >
                              Add Certificate
                            </v-btn>
                          </div>
                          
                          <div 
                            v-for="(cert, index) in editedKeybox.rsaCerts" 
                            :key="`rsa-cert-${index}`"
                            class="d-flex align-center mb-2"
                          >
                            <v-textarea
                              v-model="editedKeybox.rsaCerts[index]"
                              label="Certificate"
                              rows="3"
                              class="mr-2"
                            ></v-textarea>
                            
                            <v-btn
                              icon
                              color="error"
                              @click="removeCertificate('rsa', index)"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                          
                          <v-alert
                            v-if="!editedKeybox.rsaCerts.length"
                            type="info"
                            text
                            dense
                          >
                            No RSA certificates added
                          </v-alert>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
                
                <!-- Build Properties Section -->
                <v-col cols="12">
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        Build Properties
                        <template #actions>
                          <v-icon color="primary">mdi-android</v-icon>
                        </template>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-alert
                          text
                          type="info"
                          class="mb-3"
                        >
                          Build properties allow modification of Android system properties on entitled devices.
                          Ensure there's an equal number of comma-separated values in both fields.
                        </v-alert>
                        
                        <!-- Properties to Edit -->
                        <v-textarea
                          v-model="editedKeybox.propsToEdit"
                          label="Properties to Edit"
                          rows="3"
                          hint="Comma-separated list of Android system property keys (e.g., MANUFACTURER,MODEL,BRAND)"
                          persistent-hint
                          :rules="[validatePropsMapping]"
                        ></v-textarea>
                        
                        <!-- Property Values -->
                        <v-textarea
                          v-model="editedKeybox.propsValues"
                          label="Property Values"
                          rows="3"
                          hint="Comma-separated values corresponding to the keys above (e.g., google,Pixel 9,google)"
                          persistent-hint
                          class="mt-3"
                          :rules="[validatePropsMapping]"
                        ></v-textarea>
                        
                        <!-- Validation Result -->
                        <div class="mt-2">
                          <v-chip
                            small
                            :color="propsValidation.isValid ? 'success' : 'error'"
                            text-color="white"
                            v-if="propsValidation.message"
                          >
                            {{ propsValidation.message }}
                          </v-chip>
                        </div>
                        
                        <!-- Visualization (Optional) -->
                        <div class="mt-4" v-if="showBuildPropsPreview">
                          <v-divider class="mb-3"></v-divider>
                          <h4 class="text-subtitle-1 mb-2">Property Mapping Preview</h4>
                          
                          <v-simple-table dense>
                            <template #default>
                              <thead>
                                <tr>
                                  <th>Property</th>
                                  <th>Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(pair, index) in propsMappingPairs" :key="index">
                                  <td>{{ pair.key }}</td>
                                  <td>{{ pair.value }}</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </div>
                        
                        <v-btn
                          small
                          text
                          color="primary"
                          @click="showBuildPropsPreview = !showBuildPropsPreview"
                          class="mt-3"
                        >
                          {{ showBuildPropsPreview ? 'Hide Preview' : 'Show Preview' }}
                        </v-btn>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            color="blue darken-1" 
            text 
            @click="keyboxDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn 
            color="primary"
            :disabled="!formValid"
            @click="saveKeybox"
            :loading="saving"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Confirmation Dialog -->
    <v-dialog
      v-model="confirmDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title class="text-h5">{{ confirmTitle }}</v-card-title>
        <v-card-text>{{ confirmMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="error" v-if="confirmAction === 'delete'" @click="deleteKeybox">Delete</v-btn>
          <v-btn color="primary" v-if="confirmAction === 'setCurrent'" @click="setAsCurrent">Set as Current</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="4000"
    >
      {{ snackbarText }}
      <template #action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- JSON Import Dialog -->
    <v-dialog
      v-model="showJsonImportDialog"
      max-width="800px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Import Keybox from JSON</span>
        </v-card-title>
        
        <v-card-text>
          <p class="mb-2">Paste a JSON object with keybox data to auto-fill the form.</p>
          <v-textarea
            v-model="jsonImportText"
            outlined
            rows="10"
            label="JSON Data"
            placeholder="Paste keybox JSON here..."
            hint="Example format: { 'name': 'Keybox Name', 'ecPriv': 'EC.PRIV:xyz...', 'ecCerts': ['EC.CERT_1:abc...'], ... }"
            persistent-hint
          ></v-textarea>
          
          <v-alert
            v-if="jsonImportError"
            type="error"
            text
            class="mt-4"
          >
            {{ jsonImportError }}
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showJsonImportDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="importJsonData"
            :disabled="!jsonImportText.trim()"
          >
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { clientApi } from '@/services/api'

export default {
  name: 'KeyboxesRoute',
  
  data() {
    return {
      keyboxes: [],
      loading: false,
      totalItems: 0,
      itemsPerPage: 10,
      
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Description', value: 'description' },
        { text: 'Created', value: 'createdAt' },
        { text: 'Last Updated', value: 'updatedAt' },
        { text: 'Status', value: 'isCurrent' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      
      // Dialog state
      keyboxDialog: false,
      editMode: false,
      formValid: false,
      editedKeybox: this.getDefaultKeybox(),
      currentKeybox: null,
      saving: false,
      
      // Confirmation dialog
      confirmDialog: false,
      confirmTitle: '',
      confirmMessage: '',
      confirmAction: null,
      selectedKeybox: null,
      
      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      
      // JSON Import
      showJsonImportDialog: false,
      jsonImportText: '',
      jsonImportError: '',
      
      // Build Properties
      showBuildPropsPreview: false,
      propsValidation: {
        isValid: true,
        message: ''
      }
    }
  },
  
  computed: {
    // Compute key-value pairs for build properties preview
    propsMappingPairs() {
      if (!this.editedKeybox.propsToEdit || !this.editedKeybox.propsValues) {
        return [];
      }
      
      const keys = this.editedKeybox.propsToEdit.split(',').map(k => k.trim());
      const values = this.editedKeybox.propsValues.split(',').map(v => v.trim());
      
      // Create pairs up to the shorter array length
      const pairs = [];
      const minLength = Math.min(keys.length, values.length);
      
      for (let i = 0; i < minLength; i++) {
        pairs.push({
          key: keys[i],
          value: values[i]
        });
      }
      
      return pairs;
    }
  },
  
  created() {
    this.fetchKeyboxes()
  },
  
  methods: {
    getDefaultKeybox() {
      const now = new Date();
      const formattedDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const formattedTime = now.toTimeString().split(' ')[0]; // HH:MM:SS
      
      // Default build properties as specified in requirements
      const defaultPropsToEdit = "MANUFACTURER,MODEL,FINGERPRINT,BRAND,PRODUCT,DEVICE,VERSION.RELEASE,ID,VERSION.INCREMENTAL,TYPE,TAGS,VERSION.SECURITY_PATCH,VERSION.DEVICE_INITIAL_SDK_INT";
      const defaultPropsValues = "google,Pixel 9,google/tokay_beta/tokay:16/BP22.250325.007/13352765:user/release-keys,google,tokay_beta,tokay,16,BP22.250325.007,13352765,user,release-keys,2025-04-05,21";
      
      return {
        name: `Keybox ${formattedDate}`,
        description: `Created on ${formattedDate} at ${formattedTime}`,
        isCurrent: false,
        ecPriv: '',
        ecCerts: [],
        rsaPriv: '',
        rsaCerts: [],
        // Build properties fields
        propsToEdit: defaultPropsToEdit,
        propsValues: defaultPropsValues
      }
    },
    
    async fetchKeyboxes() {
      this.loading = true
      
      try {
        const response = await clientApi.getKeyboxes()
        this.keyboxes = response.data
        this.totalItems = this.keyboxes.length
        
        // Find current keybox
        this.currentKeybox = this.keyboxes.find(k => k.isCurrent)
      } catch (error) {
        console.error('Error fetching keyboxes:', error)
        this.showSnackbar('Failed to load keyboxes', 'error')
      } finally {
        this.loading = false
      }
    },
    
    viewKeybox(keybox) {
      // Navigate to keybox detail view
      this.$router.push({ name: 'KeyboxDetail', params: { id: keybox._id } })
    },
    
    openKeyboxDialog(keybox = null) {
      if (keybox) {
        // Edit mode
        this.editMode = true
        this.editedKeybox = { ...keybox }
        
        // Make sure certificates are arrays even if empty
        if (!this.editedKeybox.ecCerts) this.editedKeybox.ecCerts = []
        if (!this.editedKeybox.rsaCerts) this.editedKeybox.rsaCerts = []
      } else {
        // Create mode
        this.editMode = false
        this.editedKeybox = this.getDefaultKeybox()
      }
      
      this.keyboxDialog = true
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },
    
    async saveKeybox() {
      if (!this.$refs.form.validate()) return
      
      this.saving = true
      
      try {
        // Auto-fill name and description if empty
        const defaultKeybox = this.getDefaultKeybox();
        if (!this.editedKeybox.name.trim()) {
          this.editedKeybox.name = defaultKeybox.name;
        }
        
        if (!this.editedKeybox.description.trim()) {
          this.editedKeybox.description = defaultKeybox.description;
        }
        
        if (this.editMode) {
          // Update existing keybox
          const { _id, ...keyboxData } = this.editedKeybox
          await clientApi.updateKeybox(_id, keyboxData)
          this.showSnackbar('Keybox updated successfully')
        } else {
          // Create new keybox
          await clientApi.createKeybox(this.editedKeybox)
          this.showSnackbar('New keybox created successfully')
        }
        
        this.keyboxDialog = false
        this.fetchKeyboxes() // Refresh the list
      } catch (error) {
        console.error('Error saving keybox:', error)
        this.showSnackbar(`Failed to save keybox: ${error.message}`, 'error')
      } finally {
        this.saving = false
      }
    },
    
    confirmDelete(keybox) {
      this.selectedKeybox = keybox
      this.confirmTitle = 'Delete Keybox'
      this.confirmMessage = `Are you sure you want to delete the keybox "${keybox.name}"? This action cannot be undone.`
      this.confirmAction = 'delete'
      this.confirmDialog = true
    },
    
    async deleteKeybox() {
      try {
        await clientApi.deleteKeybox(this.selectedKeybox._id)
        this.confirmDialog = false
        this.showSnackbar('Keybox deleted successfully')
        this.fetchKeyboxes() // Refresh the list
      } catch (error) {
        console.error('Error deleting keybox:', error)
        this.showSnackbar(`Failed to delete keybox: ${error.message}`, 'error')
      }
    },
    
    confirmSetCurrent(keybox) {
      this.selectedKeybox = keybox
      this.confirmTitle = 'Set as Current Keybox'
      this.confirmMessage = `Are you sure you want to set "${keybox.name}" as the current keybox? This will mark all other keyboxes as not current.`
      this.confirmAction = 'setCurrent'
      this.confirmDialog = true
    },
    
    async setAsCurrent() {
      try {
        await clientApi.setKeyboxAsCurrent(this.selectedKeybox._id)
        this.confirmDialog = false
        this.showSnackbar(`${this.selectedKeybox.name} is now the current keybox`)
        this.fetchKeyboxes() // Refresh the list
      } catch (error) {
        console.error('Error setting current keybox:', error)
        this.showSnackbar(`Failed to set current keybox: ${error.message}`, 'error')
      }
    },
    
    addCertificate(type) {
      if (type === 'ec') {
        this.editedKeybox.ecCerts.push('')
      } else if (type === 'rsa') {
        this.editedKeybox.rsaCerts.push('')
      }
    },
    
    removeCertificate(type, index) {
      if (type === 'ec') {
        this.editedKeybox.ecCerts.splice(index, 1)
      } else if (type === 'rsa') {
        this.editedKeybox.rsaCerts.splice(index, 1)
      }
    },
    
    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
    
    validatePropsMapping() {
      // Skip validation if either field is empty
      if (!this.editedKeybox.propsToEdit || !this.editedKeybox.propsValues) {
        this.propsValidation = {
          isValid: true,
          message: ''
        };
        return true;
      }
      
      const keys = this.editedKeybox.propsToEdit.split(',').map(k => k.trim());
      const values = this.editedKeybox.propsValues.split(',').map(v => v.trim());
      
      // Check if keys or values are empty
      if (keys.length === 0 || values.length === 0) {
        this.propsValidation = {
          isValid: false,
          message: 'Both fields must contain comma-separated values'
        };
        return this.propsValidation.message;
      }
      
      // Check if there's an equal number of keys and values
      if (keys.length !== values.length) {
        this.propsValidation = {
          isValid: false,
          message: `Mismatch: ${keys.length} keys vs ${values.length} values`
        };
        return this.propsValidation.message;
      }
      
      // Check for empty items in either array
      const emptyKeys = keys.some(k => k === '');
      const emptyValues = values.some(v => v === '');
      
      if (emptyKeys || emptyValues) {
        this.propsValidation = {
          isValid: false,
          message: 'Empty items found in comma-separated list'
        };
        return this.propsValidation.message;
      }
      
      // Validation passed
      this.propsValidation = {
        isValid: true,
        message: `${keys.length} properties mapped correctly`
      };
      
      return true;
    },
    
    importJsonData() {
      this.jsonImportError = '';
      
      try {
        // Parse the JSON string into an object
        let importedData;
        
        try {
          importedData = JSON.parse(this.jsonImportText);
        } catch (e) {
          // If standard JSON parsing fails, try evaluating as a JavaScript object literal
          // This is more forgiving for things like unquoted properties or single quotes
          try {
            // Using Function instead of eval for better security
            // This allows parsing of JS object literals that may not be strict JSON
            importedData = Function(`'use strict'; return (${this.jsonImportText})`)();
          } catch (evalError) {
            throw new Error('Invalid JSON format. Please check your input.');
          }
        }
        
        if (!importedData || typeof importedData !== 'object') {
          throw new Error('Invalid data format. Expected a JSON object.');
        }
        
        // Valid keybox properties that we want to import
        const validProps = [
          'name', 'description', 'isCurrent', 
          'ecPriv', 'ecCerts', 'rsaPriv', 'rsaCerts',
          'propsToEdit', 'propsValues'
        ];
        
        // Update the editedKeybox with valid properties from the imported data
        validProps.forEach(prop => {
          if (importedData[prop] !== undefined) {
            // For certificate arrays, ensure they're arrays even if provided as a single string
            if ((prop === 'ecCerts' || prop === 'rsaCerts') && !Array.isArray(importedData[prop])) {
              if (typeof importedData[prop] === 'string') {
                this.editedKeybox[prop] = [importedData[prop]];
              }
            } else {
              this.editedKeybox[prop] = importedData[prop];
            }
          }
        });
        
        // Handle the keyboxData format from the spec (nested structure)
        if (importedData.keyboxData) {
          const kd = importedData.keyboxData;
          
          // Handle EC keys
          if (kd.ec) {
            if (kd.ec.priv) this.editedKeybox.ecPriv = kd.ec.priv;
            if (kd.ec.certs) this.editedKeybox.ecCerts = Array.isArray(kd.ec.certs) ? kd.ec.certs : [kd.ec.certs];
          }
          
          // Handle RSA keys
          if (kd.rsa) {
            if (kd.rsa.priv) this.editedKeybox.rsaPriv = kd.rsa.priv;
            if (kd.rsa.certs) this.editedKeybox.rsaCerts = Array.isArray(kd.rsa.certs) ? kd.rsa.certs : [kd.rsa.certs];
          }
          
          // Handle build properties
          if (kd.buildProps) {
            if (kd.buildProps.propsToEdit) this.editedKeybox.propsToEdit = kd.buildProps.propsToEdit;
            if (kd.buildProps.propsValues) this.editedKeybox.propsValues = kd.buildProps.propsValues;
          }
        }
        
        // Close the dialog and show success message
        this.showJsonImportDialog = false;
        this.showSnackbar('Keybox data imported successfully');
        
      } catch (error) {
        // Show the error message
        this.jsonImportError = error.message;
        console.error('JSON import error:', error);
      }
    }
  }
}
</script>

<style scoped>
/* Add any component-specific styles here */
</style>